import { useState } from 'react';
import { Target, MapPin, Clock, Zap, Filter } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Input';
import { useOpportunities } from '@/hooks/useOpportunities';
import { VEHICLE_CATEGORY_LABELS } from '@/types';
import type { VehicleCategory, Opportunity } from '@/types';
import toast from 'react-hot-toast';

export function OpportunitiesPage() {
  const [filters, setFilters] = useState<{ vehicle_category?: string }>({});
  const { opportunities, applications, loading, apply } = useOpportunities(filters);
  const [selectedOpp, setSelectedOpp] = useState<Opportunity | null>(null);
  const [applying, setApplying] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  if (loading) return <LoadingSpinner />;

  const appliedIds = new Set(applications.map(a => a.opportunity_id));

  const handleApply = async (oppId: string) => {
    try {
      setApplying(true);
      await apply(oppId, {});
      toast.success('¡Aplicación enviada! 🎉');
      setSelectedOpp(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al aplicar');
    } finally {
      setApplying(false);
    }
  };

  const getPriorityIcon = (priority: string) => {
    if (priority === 'urgent') return <Zap size={14} className="text-red-500" />;
    if (priority === 'high') return <Zap size={14} className="text-orange-500" />;
    return null;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl tracking-wider">OPORTUNIDADES</h1>
        <Button variant="outline" size="sm" onClick={() => setShowFilters(true)}>
          <Filter size={14} className="mr-1" /> Filtros
        </Button>
      </div>

      {/* My applications summary */}
      {applications.length > 0 && (
        <Card className="bg-brand-black text-brand-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-brand-yellow font-heading text-lg tracking-wider">MIS APLICACIONES</p>
              <p className="text-sm text-gray-400">{applications.length} oportunidades aplicadas</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-brand-yellow">{applications.filter(a => a.status === 'accepted').length}</p>
              <p className="text-xs text-gray-400">aceptadas</p>
            </div>
          </div>
        </Card>
      )}

      {/* Opportunities list */}
      {opportunities.length === 0 ? (
        <EmptyState
          title="Sin oportunidades"
          description="No hay oportunidades disponibles en este momento. Vuelve pronto."
          icon={<Target size={28} className="text-gray-400" />}
        />
      ) : (
        <div className="space-y-3">
          {opportunities.map(opp => {
            const isApplied = appliedIds.has(opp.id);
            const isNew = new Date(opp.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000);

            return (
              <Card key={opp.id} hover onClick={() => setSelectedOpp(opp)} className="relative">
                {isNew && (
                  <span className="absolute top-2 right-2 text-[10px] bg-brand-yellow px-2 py-0.5 rounded-full font-bold">NUEVO</span>
                )}
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-brand-black rounded-xl flex items-center justify-center flex-shrink-0">
                    <Target size={20} className="text-brand-yellow" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {getPriorityIcon(opp.priority)}
                      <p className="text-sm font-semibold truncate">{opp.title}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <Badge label={VEHICLE_CATEGORY_LABELS[opp.vehicle_category as VehicleCategory] || opp.vehicle_category} variant="black" size="sm" />
                      {opp.zone_name && (
                        <span className="text-xs text-gray-500 flex items-center gap-0.5">
                          <MapPin size={10} /> {opp.zone_name}
                        </span>
                      )}
                      {opp.duration && (
                        <span className="text-xs text-gray-500 flex items-center gap-0.5">
                          <Clock size={10} /> {opp.duration}
                        </span>
                      )}
                    </div>
                    {opp.estimated_pay_range && (
                      <p className="text-sm font-bold text-brand-black mt-1">{opp.estimated_pay_range}</p>
                    )}
                  </div>
                  {isApplied && <StatusBadge status="applied" />}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <span>{opp.slots_filled}/{opp.slots_total} cubiertos</span>
                    {opp.route_type && <span>• {opp.route_type}</span>}
                  </div>
                  <StatusBadge status={opp.priority} showDot={false} />
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Opportunity detail modal */}
      <Modal isOpen={!!selectedOpp} onClose={() => setSelectedOpp(null)} title={selectedOpp?.title} size="lg">
        {selectedOpp && (
          <div className="space-y-4">
            {selectedOpp.description && (
              <p className="text-sm text-gray-600">{selectedOpp.description}</p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500">Vehículo</p>
                <p className="text-sm font-semibold">{VEHICLE_CATEGORY_LABELS[selectedOpp.vehicle_category as VehicleCategory]}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500">Zona</p>
                <p className="text-sm font-semibold">{selectedOpp.zone_name || 'No especificada'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500">Pago estimado</p>
                <p className="text-sm font-semibold">{selectedOpp.estimated_pay_range || 'A definir'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500">Duración</p>
                <p className="text-sm font-semibold">{selectedOpp.duration || 'A definir'}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {selectedOpp.requires_helpers && <Badge label="Requiere ayudantes" variant="warning" />}
              {selectedOpp.requires_fumigation && <Badge label="Requiere fumigación" variant="info" />}
              {selectedOpp.requires_refrigeration && <Badge label="Requiere refrigeración" variant="info" />}
            </div>

            {appliedIds.has(selectedOpp.id) ? (
              <div className="text-center py-4">
                <Badge label="Ya aplicaste a esta oportunidad" variant="success" size="md" />
              </div>
            ) : (
              <Button fullWidth onClick={() => handleApply(selectedOpp.id)} loading={applying}>
                Aplicar ahora
              </Button>
            )}
          </div>
        )}
      </Modal>

      {/* Filters modal */}
      <Modal isOpen={showFilters} onClose={() => setShowFilters(false)} title="Filtros">
        <div className="space-y-4">
          <Select
            label="Tipo de vehículo"
            value={filters.vehicle_category || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, vehicle_category: e.target.value || undefined }))}
            options={Object.entries(VEHICLE_CATEGORY_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          />
          <Button fullWidth onClick={() => setShowFilters(false)}>Aplicar filtros</Button>
        </div>
      </Modal>
    </div>
  );
}
